import { timeOffLocalInput } from '../../../shared/timeOffIntervals.js';
import { apiFetch } from './http';
import type { AppUpdateAnnouncement } from './appUpdates';
import { toAppError } from '../utils/appErrors';

export interface DashboardNotification {
  id: string;
  title: string;
  body: string | null;
  actionUrl: string | null;
  severity: 'info' | 'success' | 'warning' | 'critical' | string;
  createdAt: string;
  readAt: string | null;
  dismissedAt: string | null;
  locationId: string | null;
  locationName: string | null;
  metadata: Record<string, unknown>;
}

export interface DashboardTask {
  id: string;
  title: string;
  description: string | null;
  actionUrl: string | null;
  dueAt: string | null;
  priority: 'low' | 'normal' | 'high' | string;
  status: 'open' | 'in_progress' | 'completed' | 'dismissed' | string;
  createdAt: string;
  locationId: string | null;
  locationName: string | null;
  metadata: Record<string, unknown>;
  completionNotes?: string | null;
  taskSource?: 'system' | 'personal' | 'assigned' | string;
  createdByName?: string | null;
  assigneeName?: string | null;
}

export interface TodoTaskRow {
  task_id: string;
  title: string;
  description: string | null;
  action_url: string | null;
  due_at: string | null;
  priority: 'low' | 'normal' | 'high' | string;
  status: 'open' | 'in_progress' | 'completed' | 'dismissed' | string;
  created_at: string;
  completed_at: string | null;
  completed_by: string | null;
  completion_notes: string | null;
  task_source: 'system' | 'personal' | 'assigned' | string;
  metadata: Record<string, unknown>;
  createdByName?: string | null;
  assigneeName?: string | null;
  show_on_home?: boolean;
}

export interface TodoTasksPayload {
  personal: TodoTaskRow[];
  assignedToMe: TodoTaskRow[];
  assignedByMe: TodoTaskRow[];
}

export interface Subordinate {
  id: string;
  fullName: string;
  email: string;
}

export interface DashboardMention {
  id: string;
  messageId: string;
  eventId: string;
  createdAt: string;
  readAt: string | null;
  authorName: string | null;
  body: string | null;
  event: {
    id: string;
    name: string | null;
    code: string | null;
  } | null;
}

export interface DashboardTransferRequest {
  id: string;
  status: string;
  qty: number | null;
  neededBy: string | null;
  expectedReturn: string | null;
  fromLocation: { id: string | null; name: string | null } | null;
  toLocation: { id: string | null; name: string | null } | null;
  orderLineId: string | null;
  item: { name: string | null; productCode: string | null } | null;
  event: { id: string; name: string | null; code: string | null } | null;
}

export interface DashboardBroadcast {
  id: string;
  createdBy: string;
  createdByName: string | null;
  scope: 'all' | 'managed';
  title: string;
  body: string | null;
  createdAt: string;
  canRecall: boolean;
}

export interface DashboardPayload {
  notifications: { total: number; items: DashboardNotification[] };
  tasks: { total: number; items: DashboardTask[] };
  mentions: { unread: number; items: DashboardMention[] };
  transfers: { pending: number; items: DashboardTransferRequest[] };
  broadcasts?: { items: DashboardBroadcast[] };
  appUpdates?: { pendingReviews: AppUpdateAnnouncement[] };
}

export interface EventConfirmationDecisionResult {
  outcome: 'confirmed' | 'denied' | string;
  requestId: string | null;
  requestStatus: string | null;
  eventId: string | null;
  eventStatus: string | null;
  confirmation?: Record<string, unknown> | null;
  reservationJobStatus?: string | null;
  reservationJob?: Record<string, unknown> | null;
  [key: string]: unknown;
}

async function readDashboardError(res: Response, fallback: string): Promise<string> {
  const text = await res.text();
  if (!text) return fallback;
  try {
    const payload = JSON.parse(text) as { message?: unknown; error?: unknown };
    const message = typeof payload?.message === 'string' ? payload.message.trim() : '';
    if (message) return message;
    const error = typeof payload?.error === 'string' ? payload.error.trim() : '';
    if (error) return error;
  } catch {
    // Preserve a plain-text server response.
  }
  return text;
}

async function handleJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const detail = await readDashboardError(res, `Request failed with status ${res.status}`);
    if (res.status === 503) {
      throw toAppError(
        { status: res.status, message: detail },
        'Dashboard could not be loaded.',
        'dashboard.load',
      );
    }
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}

const NOTIFICATION_PROFILE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Normalize saved time-off links at read and render time, including cached notices. */
export function dashboardNotificationActionUrl(notification: DashboardNotification): string | null {
  const profileId = notification.metadata?.target_profile_id;
  if (!notification.metadata?.time_off_request_id || typeof profileId !== 'string'
    || !NOTIFICATION_PROFILE_UUID.test(profileId) || !notification.actionUrl?.startsWith('/calendar?')) return notification.actionUrl;
  const url = new URL(notification.actionUrl, 'https://portal.invalid');
  if (url.pathname !== '/calendar' || !['requests', 'team'].includes(url.searchParams.get('scope') || '')) return notification.actionUrl;
  if (notification.metadata.on_behalf === true) url.searchParams.set('scope', 'team');
  url.searchParams.set('person', `employee:${profileId}`);
  const startsAt = notification.metadata.starts_at;
  if (typeof startsAt === 'string') {
    const zone = notification.metadata.all_day === true && typeof notification.metadata.calendar_timezone === 'string' ? notification.metadata.calendar_timezone : undefined;
    const date = timeOffLocalInput(startsAt,zone).slice(0,10); if (date) url.searchParams.set('date',date);
  }
  const locationId = notification.metadata.site_location_id ?? notification.locationId;
  if (typeof locationId === 'string' && NOTIFICATION_PROFILE_UUID.test(locationId)) url.searchParams.set('location', locationId);
  return `${url.pathname}${url.search}${url.hash}`;
}

export async function fetchDashboard(): Promise<DashboardPayload> {
  const res = await apiFetch('/dashboard');
  const payload = await handleJson<DashboardPayload>(res);
  payload.notifications.items = payload.notifications.items.map(notification => ({
    ...notification, actionUrl: dashboardNotificationActionUrl(notification),
  }));
  return payload;
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  const res = await apiFetch(`/dashboard/notifications/${notificationId}/read`, { method: 'POST' });
  if (!res.ok) throw new Error('Failed to mark notification as read');
}

export async function dismissNotification(notificationId: string): Promise<void> {
  const res = await apiFetch(`/dashboard/notifications/${notificationId}/dismiss`, { method: 'POST' });
  if (!res.ok) throw new Error('Failed to dismiss notification');
}

export async function markAllNotificationsRead(): Promise<void> {
  const res = await apiFetch('/dashboard/notifications/read-all', { method: 'POST' });
  if (!res.ok) throw new Error('Failed to mark all notifications as read');
}

export async function dismissAllNotifications(): Promise<void> {
  const res = await apiFetch('/dashboard/notifications/dismiss-all', { method: 'POST' });
  if (!res.ok) throw new Error('Failed to dismiss all notifications');
}

export async function acknowledgeChatRead(
  messageIds: string[],
  options: { conversationId?: string | null; aiAnalysisThreadId?: string | null } = {},
): Promise<{ mentionsUpdated: number; notificationsDismissed: number }> {
  const ids = Array.isArray(messageIds)
    ? messageIds.map((id) => String(id || '').trim()).filter(Boolean)
    : [];
  const conversationId = String(options.conversationId || '').trim();
  const aiAnalysisThreadId = String(options.aiAnalysisThreadId || '').trim();
  const res = await apiFetch('/dashboard/chat/acknowledge-read', {
    method: 'POST',
    body: JSON.stringify({
      messageIds: ids,
      ...(conversationId ? { conversationId } : {}),
      ...(aiAnalysisThreadId ? { aiAnalysisThreadId } : {}),
    }),
  });
  if (!res.ok) throw new Error('Failed to acknowledge chat read state');
  const payload = await res.json() as { mentionsUpdated?: number; notificationsDismissed?: number };
  return {
    mentionsUpdated: Number(payload?.mentionsUpdated || 0),
    notificationsDismissed: Number(payload?.notificationsDismissed || 0),
  };
}

export async function completeTask(taskId: string, notes?: string | null): Promise<void> {
  const body = notes ? JSON.stringify({ notes }) : undefined;
  const res = await apiFetch(`/dashboard/tasks/${taskId}/complete`, { method: 'POST', body });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({})) as { message?: string };
    throw new Error(payload.message || 'Failed to complete task');
  }
}

export type TaskDismissDuration = 'forever' | 'day' | 'week' | 'month';

export async function dismissTask(taskId: string, duration: TaskDismissDuration): Promise<void> {
  const res = await apiFetch(`/dashboard/tasks/${encodeURIComponent(taskId)}/dismiss`, {
    method: 'POST',
    body: JSON.stringify({ duration }),
  });
  await handleJson<{ ok: boolean; dismissedUntil: string | null }>(res);
}

export async function reopenTask(taskId: string): Promise<void> {
  const res = await apiFetch(`/dashboard/tasks/${taskId}/reopen`, { method: 'POST' });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({})) as { message?: string };
    throw new Error(payload.message || 'Failed to reopen task');
  }
}

export async function decideEventConfirmationRequest(
  requestId: string,
  decision: 'approved' | 'denied',
  reason?: string | null,
): Promise<EventConfirmationDecisionResult> {
  const res = await apiFetch(`/dashboard/event-confirmations/${encodeURIComponent(requestId)}/decision`, {
    method: 'POST',
    body: JSON.stringify({
      decision,
      reason: reason?.trim() || null,
    }),
  });
  return handleJson<EventConfirmationDecisionResult>(res);
}

export async function createTodoTask(payload: {
  title: string;
  description?: string | null;
  actionUrl?: string | null;
  dueAt?: string | null;
  priority?: 'low' | 'normal' | 'high';
  targetProfileId?: string | null;
  metadata?: Record<string, unknown>;
}): Promise<{ ok: boolean; taskId: string | null }> {
  const res = await apiFetch('/dashboard/tasks', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return handleJson<{ ok: boolean; taskId: string | null }>(res);
}

export async function deleteTodoTask(taskId: string): Promise<void> {
  const res = await apiFetch(`/dashboard/tasks/${taskId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to delete task');
}

export async function fetchMyTodoTasks(opts?: { includeCompleted?: boolean }): Promise<TodoTasksPayload> {
  const qs = opts?.includeCompleted ? '?includeCompleted=true' : '';
  const res = await apiFetch(`/dashboard/tasks/mine${qs}`);
  return handleJson<TodoTasksPayload>(res);
}

export async function fetchSubordinates(): Promise<Subordinate[]> {
  const res = await apiFetch('/dashboard/subordinates');
  const payload = await handleJson<{ subordinates: Subordinate[] }>(res);
  return payload.subordinates;
}

export async function toggleTaskPin(taskId: string): Promise<{ showOnHome: boolean }> {
  const res = await apiFetch(`/dashboard/tasks/${taskId}/pin`, { method: 'POST' });
  return handleJson<{ ok: boolean; showOnHome: boolean }>(res);
}

export async function decideTransfer(transferId: string, decision: 'approve' | 'reject', note?: string | null): Promise<void> {
  const res = await apiFetch(`/dashboard/transfers/${transferId}/decision`, {
    method: 'POST',
    body: JSON.stringify({ decision, note: note ?? null }),
  });
  if (!res.ok) throw new Error('Failed to update transfer request');
}

export async function createBroadcast(payload: { title: string; body?: string | null; scope?: 'all' | 'managed' }): Promise<{ id: string; createdAt: string }> {
  const res = await apiFetch('/dashboard/broadcasts', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text();
    let message = 'Failed to create broadcast';
    try {
      const body = JSON.parse(text) as { message?: string; error?: string };
      if (body?.message) message = body.message;
      else if (body?.error) message = body.error;
    } catch {
      if (text) message = text;
    }
    throw new Error(message);
  }
  return res.json() as Promise<{ id: string; createdAt: string }>;
}

export async function recallBroadcast(broadcastId: string): Promise<void> {
  const res = await apiFetch(`/dashboard/broadcasts/${broadcastId}/recall`, { method: 'POST' });
  if (!res.ok) throw new Error('Failed to recall broadcast');
}

export async function dismissBroadcast(broadcastId: string): Promise<void> {
  const res = await apiFetch(`/dashboard/broadcasts/${broadcastId}/dismiss`, { method: 'POST' });
  if (!res.ok) throw new Error('Failed to dismiss broadcast');
}
